// Site translations. The language is chosen on the visitor's first visit
// (see the quiz gate in public/index.html) and stored in localStorage.

export const LANG_KEY = "preferredLanguage";

export const LANGUAGES = [
  { code: "en", name: "English" },
  { code: "fr", name: "Français" },
  { code: "ar", name: "العربية", rtl: true },
  { code: "it", name: "Italiano" },
  { code: "de", name: "Deutsch" },
  { code: "zh", name: "中文" },
  { code: "ja", name: "日本語" },
];

const SUPPORTED = LANGUAGES.map((l) => l.code);

export function getStoredLang() {
  try {
    const v = localStorage.getItem(LANG_KEY);
    if (SUPPORTED.includes(v)) return v;
  } catch {
    /* storage unavailable */
  }
  return "en";
}

export function applyLangToDocument(lang) {
  const info = LANGUAGES.find((l) => l.code === lang);
  document.documentElement.lang = lang;
  document.documentElement.dir = info && info.rtl ? "rtl" : "ltr";
}

export function saveLang(lang) {
  try {
    localStorage.setItem(LANG_KEY, lang);
  } catch {
    /* storage unavailable */
  }
}

// Replace {name} placeholders.
export function fmt(str, vars) {
  return str.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? vars[k] : `{${k}}`));
}

export const T = {
  en: {
    nav: { experience: "Experience", projects: "Projects", skills: "Skills", education: "Education" },
    email: "Email",
    language: "Language",
    toggleTheme: "Toggle theme",
    toggleMenu: "Toggle menu",
    closeMenu: "Close menu",
    prank: "☀️ Light mode!! not in my domain",
    tagline: "Master's in Artificial Intelligence • Software engineer building AI‑powered, web, mobile and desktop solutions.",
    heroText: "I'm a passionate software engineer from Algeria who loves creating innovative digital solutions. I specialize in JavaScript, web development, machine learning, and solving challenging programming problems.",
    heroCaption: "Coding gives you the power to create.",
    contributions: "contributions",
    loadingGraph: "Loading contribution graph…",
    loadingActivity: "Loading activity…",
    couldNotLoad: "Could not load.",
    viewOnGitHub: "View on GitHub →",
    viewAllActivity: "View all activity on GitHub →",
    contribCaption: "{n} contributions in the last year",
    contribCell: "{date}: {n} contributions",
    recentActivity: "Recent GitHub Activity",
    gh: {
      pushed: "Pushed {n} commit(s) to",
      created: "Created {type} in",
      branch: "Branch: {name}",
      starred: "Starred",
      forked: "Forked",
      issue: "Updated an issue in",
      pr: "Updated a pull request in",
      other: "{type} in",
    },
    aboutTitle: "About & Experience",
    aboutCardTitle: "Software Engineer & AI Developer",
    aboutP1: "My journey in technology started with curiosity and has evolved into a professional pursuit of excellence. I thrive on solving complex problems and bringing ideas to life through code.",
    aboutP2: "I work across artificial intelligence, web development, mobile development, and reverse engineering, always aiming to build impactful and robust solutions.",
    aboutList: [
      "Designing and implementing AI‑powered features and services.",
      "Building full‑stack web applications with modern tooling.",
      "Creating educational and experimental tools for developers and learners.",
    ],
    projectsTitle: "Things I've built",
    categories: { web: "Web Applications", mobile: "Mobile Applications" },
    details: "Details",
    viewProject: "View project →",
    visitProject: "Visit project →",
    clickForDetails: "Click for details",
    openDetails: "Open {title} details",
    comingSoon: "Coming soon",
    soon: "SOON",
    soundOn: "Turn sound on",
    soundOff: "Turn sound off",
    close: "Close",
    projects: {
      android: {
        subtitle: "Interactive Android Boot Sequence",
        description: "An interactive Android boot sequence simulator with step-by-step visualization, terminal interface, and educational content about Android system initialization.",
        details: [
          "Step-by-step visualization of each boot stage, from bootloader to the home screen.",
          "Built-in terminal interface that prints the boot logs as they happen.",
          "Educational notes explaining what Android does at every step.",
        ],
      },
      youngme: {
        subtitle: "Job matching platform",
        description: "YoungME Lavoro is a platform that matches young candidates with companies and the Public Administration, helping them find the right job opportunities.",
        details: [
          "Matches candidates with companies and the Public Administration.",
          "Separate spaces for candidates, companies and public bodies.",
        ],
      },
      fantazanca: {
        subtitle: "Multiplatform fantasy game",
        description: "FantaZanca is the first fantasy game dedicated to the 2026 Messina municipal elections. Join the game, build your team by choosing from real candidates, follow the electoral campaign, and climb the city leaderboard.",
        details: [
          "Build your team by choosing from the real election candidates.",
          "Follow the electoral campaign and earn points.",
          "Climb the city leaderboard against other players.",
        ],
      },
      tripsters: {
        subtitle: "Travel like a local",
        description: "Tripsters builds tailor-made travel itineraries told by locals, from the heart of Messina to the rest of the world. Chat with and meet the people who really live there.",
        details: [
          "Travellers discover verified local guides and chat or call them in real time.",
          "Credit-based wallet to pay for chats and calls.",
          "In-app AI travel assistant alongside the human guides.",
          "Ratings and a ranking score that reward the best locals.",
        ],
      },
    },
    skillsTitle: "The magic behind",
    skillGroups: {
      ml: "Machine Learning & AI",
      algo: "Algorithms & Classification",
      bigdata: "Big Data",
      langs: "Programming Languages",
      web: "Web Development",
      security: "Cybersecurity",
      db: "Database Management",
      linux: "Linux Systems",
      tools: "Tools & Frameworks",
      other: "Other Skills",
    },
    backgroundTitle: "Background",
    eduTitle: "Master's in Artificial Intelligence",
    eduText: "University of Skikda, Algeria • Focus on advanced machine learning, deep learning architectures, and AI algorithms.",
    footerTitle: "Let's work together",
    footerText: "Open to discussing new projects, creative ideas, or opportunities to build impactful AI‑powered products.",
    emailMe: "Email Me",
  },

  fr: {
    nav: { experience: "Expérience", projects: "Projets", skills: "Compétences", education: "Formation" },
    email: "E-mail",
    language: "Langue",
    toggleTheme: "Changer de thème",
    toggleMenu: "Ouvrir le menu",
    closeMenu: "Fermer le menu",
    prank: "☀️ Le mode clair ?! Pas chez moi",
    tagline: "Master en intelligence artificielle • Ingénieur logiciel qui crée des solutions web, mobiles et desktop basées sur l'IA.",
    heroText: "Je suis un ingénieur logiciel passionné, originaire d'Algérie, qui aime créer des solutions numériques innovantes. Je suis spécialisé en JavaScript, développement web, apprentissage automatique et résolution de problèmes de programmation complexes.",
    heroCaption: "Coder, c'est avoir le pouvoir de créer.",
    contributions: "contributions",
    loadingGraph: "Chargement du graphique des contributions…",
    loadingActivity: "Chargement de l'activité…",
    couldNotLoad: "Chargement impossible.",
    viewOnGitHub: "Voir sur GitHub →",
    viewAllActivity: "Voir toute l'activité sur GitHub →",
    contribCaption: "{n} contributions au cours de l'année passée",
    contribCell: "{date} : {n} contributions",
    recentActivity: "Activité GitHub récente",
    gh: {
      pushed: "{n} commit(s) envoyé(s) vers",
      created: "{type} créé(e) dans",
      branch: "Branche : {name}",
      starred: "Ajouté aux favoris",
      forked: "Forké",
      issue: "Issue mise à jour dans",
      pr: "Pull request mise à jour dans",
      other: "{type} dans",
    },
    aboutTitle: "À propos & expérience",
    aboutCardTitle: "Ingénieur logiciel & développeur IA",
    aboutP1: "Mon parcours dans la technologie a commencé par la curiosité et s'est transformé en une quête professionnelle de l'excellence. J'aime résoudre des problèmes complexes et donner vie aux idées grâce au code.",
    aboutP2: "Je travaille dans l'intelligence artificielle, le développement web, le développement mobile et la rétro-ingénierie, avec toujours l'objectif de créer des solutions utiles et robustes.",
    aboutList: [
      "Concevoir et mettre en œuvre des fonctionnalités et services basés sur l'IA.",
      "Développer des applications web full-stack avec des outils modernes.",
      "Créer des outils pédagogiques et expérimentaux pour les développeurs et les apprenants.",
    ],
    projectsTitle: "Mes réalisations",
    categories: { web: "Applications web", mobile: "Applications mobiles" },
    details: "Détails",
    viewProject: "Voir le projet →",
    visitProject: "Visiter le projet →",
    clickForDetails: "Cliquez pour les détails",
    openDetails: "Ouvrir les détails de {title}",
    comingSoon: "Bientôt disponible",
    soon: "BIENTÔT",
    soundOn: "Activer le son",
    soundOff: "Couper le son",
    close: "Fermer",
    projects: {
      android: {
        subtitle: "Séquence de démarrage Android interactive",
        description: "Un simulateur interactif du démarrage d'Android, avec une visualisation étape par étape, une interface terminal et du contenu pédagogique sur l'initialisation du système Android.",
        details: [
          "Visualisation étape par étape de chaque phase du démarrage, du bootloader jusqu'à l'écran d'accueil.",
          "Interface terminal intégrée qui affiche les journaux de démarrage en temps réel.",
          "Notes pédagogiques qui expliquent ce que fait Android à chaque étape.",
        ],
      },
      youngme: {
        subtitle: "Plateforme de mise en relation pour l'emploi",
        description: "YoungME Lavoro est une plateforme qui met en relation les jeunes candidats avec les entreprises et l'administration publique, pour les aider à trouver les bonnes opportunités d'emploi.",
        details: [
          "Met en relation les candidats avec les entreprises et l'administration publique.",
          "Espaces séparés pour les candidats, les entreprises et les organismes publics.",
        ],
      },
      fantazanca: {
        subtitle: "Jeu de fantasy multiplateforme",
        description: "FantaZanca est le premier jeu de fantasy consacré aux élections municipales 2026 de Messine. Rejoignez le jeu, formez votre équipe avec de vrais candidats, suivez la campagne électorale et grimpez dans le classement de la ville.",
        details: [
          "Formez votre équipe en choisissant parmi les vrais candidats.",
          "Suivez la campagne électorale et gagnez des points.",
          "Grimpez dans le classement de la ville face aux autres joueurs.",
        ],
      },
      tripsters: {
        subtitle: "Voyagez comme un local",
        description: "Tripsters crée des itinéraires de voyage sur mesure racontés par des locaux, du cœur de Messine jusqu'au reste du monde. Discutez avec les personnes qui vivent vraiment sur place et rencontrez-les.",
        details: [
          "Les voyageurs découvrent des guides locaux vérifiés et peuvent discuter ou les appeler en temps réel.",
          "Portefeuille de crédits pour payer les discussions et les appels.",
          "Assistant de voyage IA intégré, aux côtés des guides humains.",
          "Notes et score de classement qui récompensent les meilleurs locaux.",
        ],
      },
    },
    skillsTitle: "La magie derrière",
    skillGroups: {
      ml: "Apprentissage automatique & IA",
      algo: "Algorithmes & classification",
      bigdata: "Big Data",
      langs: "Langages de programmation",
      web: "Développement web",
      security: "Cybersécurité",
      db: "Gestion de bases de données",
      linux: "Systèmes Linux",
      tools: "Outils & frameworks",
      other: "Autres compétences",
    },
    backgroundTitle: "Formation",
    eduTitle: "Master en intelligence artificielle",
    eduText: "Université de Skikda, Algérie • Spécialisation en apprentissage automatique avancé, architectures d'apprentissage profond et algorithmes d'IA.",
    footerTitle: "Travaillons ensemble",
    footerText: "Ouvert à la discussion de nouveaux projets, d'idées créatives ou d'opportunités pour créer des produits IA utiles.",
    emailMe: "M'écrire",
  },

  ar: {
    nav: { experience: "الخبرة", projects: "المشاريع", skills: "المهارات", education: "التعليم" },
    email: "البريد",
    language: "اللغة",
    toggleTheme: "تبديل المظهر",
    toggleMenu: "فتح القائمة",
    closeMenu: "إغلاق القائمة",
    prank: "☀️ الوضع الفاتح؟! ليس في موقعي",
    tagline: "ماجستير في الذكاء الاصطناعي • مهندس برمجيات يبني حلولًا للويب والهاتف وسطح المكتب مدعومة بالذكاء الاصطناعي.",
    heroText: "أنا مهندس برمجيات شغوف من الجزائر، أحب ابتكار حلول رقمية جديدة. أتخصص في JavaScript وتطوير الويب وتعلم الآلة وحل المشكلات البرمجية الصعبة.",
    heroCaption: "البرمجة تمنحك القدرة على الإبداع.",
    contributions: "المساهمات",
    loadingGraph: "جارٍ تحميل مخطط المساهمات…",
    loadingActivity: "جارٍ تحميل النشاط…",
    couldNotLoad: "تعذر التحميل.",
    viewOnGitHub: "عرض على GitHub ←",
    viewAllActivity: "عرض كل النشاط على GitHub ←",
    contribCaption: "{n} مساهمة خلال السنة الماضية",
    contribCell: "{date}: {n} مساهمة",
    recentActivity: "آخر نشاط على GitHub",
    gh: {
      pushed: "أرسل {n} تعديلات إلى",
      created: "أنشأ {type} في",
      branch: "الفرع: {name}",
      starred: "أضاف نجمة إلى",
      forked: "أنشأ نسخة من",
      issue: "حدّث مشكلة في",
      pr: "حدّث طلب دمج في",
      other: "{type} في",
    },
    aboutTitle: "نبذة وخبرة",
    aboutCardTitle: "مهندس برمجيات ومطور ذكاء اصطناعي",
    aboutP1: "بدأت رحلتي في التكنولوجيا بالفضول، ثم تحولت إلى سعي مهني نحو التميز. أستمتع بحل المشكلات المعقدة وتحويل الأفكار إلى واقع عبر البرمجة.",
    aboutP2: "أعمل في مجالات الذكاء الاصطناعي وتطوير الويب وتطوير تطبيقات الهاتف والهندسة العكسية، وأسعى دائمًا إلى بناء حلول مؤثرة ومتينة.",
    aboutList: [
      "تصميم وتنفيذ ميزات وخدمات مدعومة بالذكاء الاصطناعي.",
      "بناء تطبيقات ويب متكاملة بأدوات حديثة.",
      "إنشاء أدوات تعليمية وتجريبية للمطورين والمتعلمين.",
    ],
    projectsTitle: "ما قمت ببنائه",
    categories: { web: "تطبيقات الويب", mobile: "تطبيقات الهاتف" },
    details: "التفاصيل",
    viewProject: "عرض المشروع ←",
    visitProject: "زيارة المشروع ←",
    clickForDetails: "انقر لعرض التفاصيل",
    openDetails: "فتح تفاصيل {title}",
    comingSoon: "قريبًا",
    soon: "قريبًا",
    soundOn: "تشغيل الصوت",
    soundOff: "كتم الصوت",
    close: "إغلاق",
    projects: {
      android: {
        subtitle: "تسلسل إقلاع أندرويد التفاعلي",
        description: "محاكي تفاعلي لعملية إقلاع نظام أندرويد، مع عرض مرئي خطوة بخطوة وواجهة طرفية ومحتوى تعليمي عن تهيئة النظام.",
        details: [
          "عرض مرئي خطوة بخطوة لكل مرحلة من مراحل الإقلاع، من محمّل الإقلاع حتى الشاشة الرئيسية.",
          "واجهة طرفية مدمجة تعرض سجلات الإقلاع لحظة بلحظة.",
          "ملاحظات تعليمية تشرح ما يفعله أندرويد في كل خطوة.",
        ],
      },
      youngme: {
        subtitle: "منصة للتوفيق بين الباحثين عن عمل وأصحاب العمل",
        description: "YoungME Lavoro منصة تربط الشباب الباحثين عن عمل بالشركات والإدارة العامة، لمساعدتهم على إيجاد فرص العمل المناسبة.",
        details: [
          "تربط المرشحين بالشركات والإدارة العامة.",
          "مساحات منفصلة للمرشحين والشركات والجهات العامة.",
        ],
      },
      fantazanca: {
        subtitle: "لعبة فانتازي متعددة المنصات",
        description: "FantaZanca أول لعبة فانتازي مخصصة للانتخابات البلدية في مدينة ميسينا عام 2026. انضم إلى اللعبة، وكوّن فريقك من المرشحين الحقيقيين، وتابع الحملة الانتخابية، وتقدّم في ترتيب المدينة.",
        details: [
          "كوّن فريقك باختيار المرشحين الحقيقيين في الانتخابات.",
          "تابع الحملة الانتخابية واجمع النقاط.",
          "تقدّم في ترتيب المدينة أمام اللاعبين الآخرين.",
        ],
      },
      tripsters: {
        subtitle: "سافر كأنك من أهل البلد",
        description: "يصمم Tripsters برامج سفر مخصصة يرويها السكان المحليون، من قلب ميسينا إلى بقية العالم. تحدث مع من يعيشون هناك فعلًا والتقِ بهم.",
        details: [
          "يكتشف المسافرون مرشدين محليين موثّقين ويتحدثون معهم أو يتصلون بهم مباشرة.",
          "محفظة رصيد لدفع ثمن المحادثات والمكالمات.",
          "مساعد سفر بالذكاء الاصطناعي داخل التطبيق إلى جانب المرشدين.",
          "تقييمات ونقاط ترتيب تكافئ أفضل المرشدين المحليين.",
        ],
      },
    },
    skillsTitle: "السحر من وراء الكواليس",
    skillGroups: {
      ml: "تعلم الآلة والذكاء الاصطناعي",
      algo: "الخوارزميات والتصنيف",
      bigdata: "البيانات الضخمة",
      langs: "لغات البرمجة",
      web: "تطوير الويب",
      security: "الأمن السيبراني",
      db: "إدارة قواعد البيانات",
      linux: "أنظمة لينكس",
      tools: "الأدوات وأطر العمل",
      other: "مهارات أخرى",
    },
    backgroundTitle: "الخلفية الأكاديمية",
    eduTitle: "ماجستير في الذكاء الاصطناعي",
    eduText: "جامعة سكيكدة، الجزائر • تخصص في تعلم الآلة المتقدم وبنى التعلم العميق وخوارزميات الذكاء الاصطناعي.",
    footerTitle: "لنعمل معًا",
    footerText: "منفتح لمناقشة مشاريع جديدة أو أفكار مبتكرة أو فرص لبناء منتجات مؤثرة بالذكاء الاصطناعي.",
    emailMe: "راسلني",
  },

  it: {
    nav: { experience: "Esperienza", projects: "Progetti", skills: "Competenze", education: "Formazione" },
    email: "Email",
    language: "Lingua",
    toggleTheme: "Cambia tema",
    toggleMenu: "Apri il menu",
    closeMenu: "Chiudi il menu",
    prank: "☀️ Modalità chiara?! Non sul mio sito",
    tagline: "Laurea magistrale in Intelligenza Artificiale • Ingegnere del software che realizza soluzioni web, mobile e desktop basate sull'IA.",
    heroText: "Sono un ingegnere del software appassionato, originario dell'Algeria, che ama creare soluzioni digitali innovative. Sono specializzato in JavaScript, sviluppo web, machine learning e nella risoluzione di problemi di programmazione complessi.",
    heroCaption: "Programmare ti dà il potere di creare.",
    contributions: "contributi",
    loadingGraph: "Caricamento del grafico dei contributi…",
    loadingActivity: "Caricamento dell'attività…",
    couldNotLoad: "Impossibile caricare.",
    viewOnGitHub: "Vedi su GitHub →",
    viewAllActivity: "Vedi tutta l'attività su GitHub →",
    contribCaption: "{n} contributi nell'ultimo anno",
    contribCell: "{date}: {n} contributi",
    recentActivity: "Attività GitHub recente",
    gh: {
      pushed: "{n} commit inviati a",
      created: "Creato {type} in",
      branch: "Branch: {name}",
      starred: "Aggiunto ai preferiti",
      forked: "Fork di",
      issue: "Issue aggiornata in",
      pr: "Pull request aggiornata in",
      other: "{type} in",
    },
    aboutTitle: "Chi sono & esperienza",
    aboutCardTitle: "Ingegnere del software & sviluppatore IA",
    aboutP1: "Il mio percorso nella tecnologia è nato dalla curiosità ed è diventato una ricerca professionale dell'eccellenza. Mi piace risolvere problemi complessi e dare vita alle idee attraverso il codice.",
    aboutP2: "Lavoro nell'intelligenza artificiale, nello sviluppo web, nello sviluppo mobile e nel reverse engineering, puntando sempre a creare soluzioni utili e robuste.",
    aboutList: [
      "Progettare e realizzare funzionalità e servizi basati sull'IA.",
      "Sviluppare applicazioni web full-stack con strumenti moderni.",
      "Creare strumenti didattici e sperimentali per sviluppatori e studenti.",
    ],
    projectsTitle: "Cosa ho realizzato",
    categories: { web: "Applicazioni web", mobile: "Applicazioni mobile" },
    details: "Dettagli",
    viewProject: "Vedi il progetto →",
    visitProject: "Visita il progetto →",
    clickForDetails: "Clicca per i dettagli",
    openDetails: "Apri i dettagli di {title}",
    comingSoon: "Prossimamente",
    soon: "PRESTO",
    soundOn: "Attiva l'audio",
    soundOff: "Disattiva l'audio",
    close: "Chiudi",
    projects: {
      android: {
        subtitle: "Sequenza di avvio Android interattiva",
        description: "Un simulatore interattivo dell'avvio di Android, con visualizzazione passo dopo passo, interfaccia a terminale e contenuti didattici sull'inizializzazione del sistema.",
        details: [
          "Visualizzazione passo dopo passo di ogni fase di avvio, dal bootloader alla schermata home.",
          "Interfaccia a terminale integrata che mostra i log di avvio in tempo reale.",
          "Note didattiche che spiegano cosa fa Android in ogni fase.",
        ],
      },
      youngme: {
        subtitle: "Piattaforma di incontro tra domanda e offerta di lavoro",
        description: "YoungME Lavoro è una piattaforma che mette in contatto i giovani candidati con le aziende e la Pubblica Amministrazione, aiutandoli a trovare le giuste opportunità di lavoro.",
        details: [
          "Mette in contatto i candidati con le aziende e la Pubblica Amministrazione.",
          "Spazi separati per candidati, aziende ed enti pubblici.",
        ],
      },
      fantazanca: {
        subtitle: "Fantagioco multipiattaforma",
        description: "FantaZanca è il primo fantagioco dedicato alle elezioni comunali 2026 di Messina. Entra nel gioco, crea la tua squadra scegliendo tra i candidati reali, segui la campagna elettorale e scala la classifica cittadina.",
        details: [
          "Crea la tua squadra scegliendo tra i veri candidati alle elezioni.",
          "Segui la campagna elettorale e guadagna punti.",
          "Scala la classifica cittadina sfidando gli altri giocatori.",
        ],
      },
      tripsters: {
        subtitle: "Viaggia come un local",
        description: "Tripsters crea itinerari di viaggio su misura raccontati da chi vive il posto, dal cuore di Messina al resto del mondo. Chatta e incontra le persone che ci vivono davvero.",
        details: [
          "I viaggiatori scoprono guide locali verificate e possono chattare o chiamarle in tempo reale.",
          "Portafoglio a crediti per pagare chat e chiamate.",
          "Assistente di viaggio IA nell'app, accanto alle guide umane.",
          "Valutazioni e un punteggio di classifica che premiano i local migliori.",
        ],
      },
    },
    skillsTitle: "La magia dietro le quinte",
    skillGroups: {
      ml: "Machine Learning & IA",
      algo: "Algoritmi & classificazione",
      bigdata: "Big Data",
      langs: "Linguaggi di programmazione",
      web: "Sviluppo web",
      security: "Cybersecurity",
      db: "Gestione di database",
      linux: "Sistemi Linux",
      tools: "Strumenti & framework",
      other: "Altre competenze",
    },
    backgroundTitle: "Formazione",
    eduTitle: "Laurea magistrale in Intelligenza Artificiale",
    eduText: "Università di Skikda, Algeria • Specializzazione in machine learning avanzato, architetture di deep learning e algoritmi di IA.",
    footerTitle: "Lavoriamo insieme",
    footerText: "Disponibile a parlare di nuovi progetti, idee creative o opportunità per realizzare prodotti basati sull'IA che facciano la differenza.",
    emailMe: "Scrivimi",
  },

  de: {
    nav: { experience: "Erfahrung", projects: "Projekte", skills: "Fähigkeiten", education: "Ausbildung" },
    email: "E-Mail",
    language: "Sprache",
    toggleTheme: "Design wechseln",
    toggleMenu: "Menü öffnen",
    closeMenu: "Menü schließen",
    prank: "☀️ Heller Modus?! Nicht auf meiner Seite",
    tagline: "Master in Künstlicher Intelligenz • Softwareingenieur, der KI-gestützte Lösungen für Web, Mobile und Desktop entwickelt.",
    heroText: "Ich bin ein leidenschaftlicher Softwareingenieur aus Algerien und entwickle gerne innovative digitale Lösungen. Meine Schwerpunkte sind JavaScript, Webentwicklung, maschinelles Lernen und das Lösen anspruchsvoller Programmierprobleme.",
    heroCaption: "Programmieren gibt dir die Kraft, etwas zu erschaffen.",
    contributions: "Beiträge",
    loadingGraph: "Beitragsdiagramm wird geladen…",
    loadingActivity: "Aktivität wird geladen…",
    couldNotLoad: "Laden fehlgeschlagen.",
    viewOnGitHub: "Auf GitHub ansehen →",
    viewAllActivity: "Alle Aktivitäten auf GitHub ansehen →",
    contribCaption: "{n} Beiträge im letzten Jahr",
    contribCell: "{date}: {n} Beiträge",
    recentActivity: "Neueste GitHub-Aktivität",
    gh: {
      pushed: "{n} Commit(s) gepusht nach",
      created: "{type} erstellt in",
      branch: "Branch: {name}",
      starred: "Mit Stern markiert",
      forked: "Geforkt",
      issue: "Issue aktualisiert in",
      pr: "Pull Request aktualisiert in",
      other: "{type} in",
    },
    aboutTitle: "Über mich & Erfahrung",
    aboutCardTitle: "Softwareingenieur & KI-Entwickler",
    aboutP1: "Mein Weg in der Technik begann mit Neugier und hat sich zu einem professionellen Streben nach Exzellenz entwickelt. Ich löse gerne komplexe Probleme und erwecke Ideen mit Code zum Leben.",
    aboutP2: "Ich arbeite in den Bereichen Künstliche Intelligenz, Webentwicklung, mobile Entwicklung und Reverse Engineering und will immer wirkungsvolle und robuste Lösungen bauen.",
    aboutList: [
      "KI-gestützte Funktionen und Dienste konzipieren und umsetzen.",
      "Full-Stack-Webanwendungen mit modernen Werkzeugen entwickeln.",
      "Lehr- und Experimentierwerkzeuge für Entwickler und Lernende erstellen.",
    ],
    projectsTitle: "Was ich gebaut habe",
    categories: { web: "Webanwendungen", mobile: "Mobile Apps" },
    details: "Details",
    viewProject: "Projekt ansehen →",
    visitProject: "Projekt besuchen →",
    clickForDetails: "Für Details klicken",
    openDetails: "Details zu {title} öffnen",
    comingSoon: "Demnächst",
    soon: "BALD",
    soundOn: "Ton einschalten",
    soundOff: "Ton ausschalten",
    close: "Schließen",
    projects: {
      android: {
        subtitle: "Interaktive Android-Bootsequenz",
        description: "Ein interaktiver Simulator für den Android-Bootvorgang mit Schritt-für-Schritt-Visualisierung, Terminal-Oberfläche und Lerninhalten zur Systeminitialisierung.",
        details: [
          "Schritt-für-Schritt-Visualisierung jeder Bootphase, vom Bootloader bis zum Startbildschirm.",
          "Integrierte Terminal-Oberfläche, die die Boot-Logs in Echtzeit anzeigt.",
          "Lernhinweise, die erklären, was Android in jedem Schritt tut.",
        ],
      },
      youngme: {
        subtitle: "Plattform für Job-Matching",
        description: "YoungME Lavoro ist eine Plattform, die junge Bewerber mit Unternehmen und der öffentlichen Verwaltung zusammenbringt und ihnen hilft, passende Jobangebote zu finden.",
        details: [
          "Bringt Bewerber mit Unternehmen und der öffentlichen Verwaltung zusammen.",
          "Eigene Bereiche für Bewerber, Unternehmen und öffentliche Einrichtungen.",
        ],
      },
      fantazanca: {
        subtitle: "Plattformübergreifendes Fantasy-Spiel",
        description: "FantaZanca ist das erste Fantasy-Spiel zu den Kommunalwahlen 2026 in Messina. Mach mit, stelle dein Team aus echten Kandidaten zusammen, verfolge den Wahlkampf und klettere in der Rangliste der Stadt nach oben.",
        details: [
          "Stelle dein Team aus den echten Wahlkandidaten zusammen.",
          "Verfolge den Wahlkampf und sammle Punkte.",
          "Klettere in der Rangliste der Stadt gegen andere Spieler nach oben.",
        ],
      },
      tripsters: {
        subtitle: "Reisen wie ein Einheimischer",
        description: "Tripsters erstellt maßgeschneiderte Reiserouten, erzählt von Einheimischen – vom Herzen Messinas bis in den Rest der Welt. Chatte mit den Menschen, die wirklich dort leben, und triff sie.",
        details: [
          "Reisende entdecken verifizierte lokale Guides und können in Echtzeit mit ihnen chatten oder telefonieren.",
          "Guthaben-Wallet zum Bezahlen von Chats und Anrufen.",
          "KI-Reiseassistent in der App, zusätzlich zu den menschlichen Guides.",
          "Bewertungen und ein Ranking, das die besten Einheimischen belohnt.",
        ],
      },
    },
    skillsTitle: "Die Magie dahinter",
    skillGroups: {
      ml: "Maschinelles Lernen & KI",
      algo: "Algorithmen & Klassifikation",
      bigdata: "Big Data",
      langs: "Programmiersprachen",
      web: "Webentwicklung",
      security: "Cybersicherheit",
      db: "Datenbankverwaltung",
      linux: "Linux-Systeme",
      tools: "Werkzeuge & Frameworks",
      other: "Weitere Fähigkeiten",
    },
    backgroundTitle: "Werdegang",
    eduTitle: "Master in Künstlicher Intelligenz",
    eduText: "Universität Skikda, Algerien • Schwerpunkt auf fortgeschrittenem maschinellem Lernen, Deep-Learning-Architekturen und KI-Algorithmen.",
    footerTitle: "Lass uns zusammenarbeiten",
    footerText: "Offen für neue Projekte, kreative Ideen oder Chancen, wirkungsvolle KI-Produkte zu entwickeln.",
    emailMe: "Schreib mir",
  },

  zh: {
    nav: { experience: "经历", projects: "项目", skills: "技能", education: "教育" },
    email: "邮箱",
    language: "语言",
    toggleTheme: "切换主题",
    toggleMenu: "打开菜单",
    closeMenu: "关闭菜单",
    prank: "☀️ 浅色模式？！我的网站可不支持",
    tagline: "人工智能硕士 • 软件工程师，开发由 AI 驱动的网页、移动端和桌面端解决方案。",
    heroText: "我是一名来自阿尔及利亚、充满热情的软件工程师，热爱创造创新的数字解决方案。我专注于 JavaScript、网页开发、机器学习，以及解决有挑战性的编程问题。",
    heroCaption: "编程让你拥有创造的力量。",
    contributions: "贡献",
    loadingGraph: "正在加载贡献图…",
    loadingActivity: "正在加载动态…",
    couldNotLoad: "加载失败。",
    viewOnGitHub: "在 GitHub 上查看 →",
    viewAllActivity: "在 GitHub 上查看全部动态 →",
    contribCaption: "过去一年共 {n} 次贡献",
    contribCell: "{date}：{n} 次贡献",
    recentActivity: "最近的 GitHub 动态",
    gh: {
      pushed: "推送了 {n} 个提交到",
      created: "创建了 {type}，位于",
      branch: "分支：{name}",
      starred: "收藏了",
      forked: "复刻了",
      issue: "更新了 issue，位于",
      pr: "更新了拉取请求，位于",
      other: "{type}，位于",
    },
    aboutTitle: "关于我与经历",
    aboutCardTitle: "软件工程师 & AI 开发者",
    aboutP1: "我的技术之路始于好奇心，后来发展成对卓越的专业追求。我乐于解决复杂问题，并用代码把想法变成现实。",
    aboutP2: "我的工作涵盖人工智能、网页开发、移动开发和逆向工程，始终致力于打造有影响力且可靠的解决方案。",
    aboutList: [
      "设计并实现由 AI 驱动的功能和服务。",
      "使用现代工具构建全栈网页应用。",
      "为开发者和学习者打造教学与实验工具。",
    ],
    projectsTitle: "我的作品",
    categories: { web: "网页应用", mobile: "移动应用" },
    details: "详情",
    viewProject: "查看项目 →",
    visitProject: "访问项目 →",
    clickForDetails: "点击查看详情",
    openDetails: "打开 {title} 的详情",
    comingSoon: "即将推出",
    soon: "即将推出",
    soundOn: "打开声音",
    soundOff: "关闭声音",
    close: "关闭",
    projects: {
      android: {
        subtitle: "交互式安卓启动流程",
        description: "一个交互式的安卓启动流程模拟器，提供分步可视化、终端界面，以及关于安卓系统初始化的教学内容。",
        details: [
          "分步展示每个启动阶段，从引导加载程序到主屏幕。",
          "内置终端界面，实时显示启动日志。",
          "教学说明，解释安卓在每一步做了什么。",
        ],
      },
      youngme: {
        subtitle: "求职匹配平台",
        description: "YoungME Lavoro 是一个将年轻求职者与企业和公共管理部门对接的平台，帮助他们找到合适的工作机会。",
        details: [
          "将求职者与企业和公共管理部门进行匹配。",
          "为求职者、企业和公共机构提供各自独立的空间。",
        ],
      },
      fantazanca: {
        subtitle: "多平台梦幻游戏",
        description: "FantaZanca 是首款以 2026 年墨西拿市政选举为主题的梦幻游戏。加入游戏，从真实候选人中组建你的队伍，关注竞选活动，并在城市排行榜上不断攀升。",
        details: [
          "从真实的选举候选人中挑选，组建你的队伍。",
          "关注竞选活动并赚取积分。",
          "与其他玩家竞争，在城市排行榜上攀升。",
        ],
      },
      tripsters: {
        subtitle: "像当地人一样旅行",
        description: "Tripsters 打造由当地人讲述的定制旅行路线，从墨西拿的中心到世界各地。和真正生活在那里的人聊天、见面。",
        details: [
          "旅行者可以发现经过认证的当地向导，并实时与他们聊天或通话。",
          "使用积分钱包支付聊天和通话费用。",
          "应用内置 AI 旅行助手，与真人向导相互配合。",
          "评分和排名分数奖励最优秀的当地向导。",
        ],
      },
    },
    skillsTitle: "幕后的魔法",
    skillGroups: {
      ml: "机器学习与 AI",
      algo: "算法与分类",
      bigdata: "大数据",
      langs: "编程语言",
      web: "网页开发",
      security: "网络安全",
      db: "数据库管理",
      linux: "Linux 系统",
      tools: "工具与框架",
      other: "其他技能",
    },
    backgroundTitle: "教育背景",
    eduTitle: "人工智能硕士",
    eduText: "阿尔及利亚斯基克达大学 • 主攻高级机器学习、深度学习架构和 AI 算法。",
    footerTitle: "一起合作吧",
    footerText: "欢迎洽谈新项目、创意想法，或一起打造有影响力的 AI 产品。",
    emailMe: "给我发邮件",
  },

  ja: {
    nav: { experience: "経歴", projects: "プロジェクト", skills: "スキル", education: "学歴" },
    email: "メール",
    language: "言語",
    toggleTheme: "テーマを切り替え",
    toggleMenu: "メニューを開く",
    closeMenu: "メニューを閉じる",
    prank: "☀️ ライトモード？！ うちのサイトにはありません",
    tagline: "人工知能の修士 • AIを活用したWeb・モバイル・デスクトップのソリューションを開発するソフトウェアエンジニア。",
    heroText: "アルジェリア出身の情熱的なソフトウェアエンジニアで、革新的なデジタルソリューションを作るのが大好きです。JavaScript、Web開発、機械学習、そして難しいプログラミングの課題解決を得意としています。",
    heroCaption: "コードは、創造する力を与えてくれる。",
    contributions: "コントリビューション",
    loadingGraph: "コントリビューショングラフを読み込み中…",
    loadingActivity: "アクティビティを読み込み中…",
    couldNotLoad: "読み込めませんでした。",
    viewOnGitHub: "GitHubで見る →",
    viewAllActivity: "GitHubですべてのアクティビティを見る →",
    contribCaption: "過去1年間のコントリビューション：{n}件",
    contribCell: "{date}：{n}件のコントリビューション",
    recentActivity: "最近のGitHubアクティビティ",
    gh: {
      pushed: "{n}件のコミットをプッシュ：",
      created: "{type}を作成：",
      branch: "ブランチ：{name}",
      starred: "スターを付けました：",
      forked: "フォークしました：",
      issue: "Issueを更新：",
      pr: "プルリクエストを更新：",
      other: "{type}：",
    },
    aboutTitle: "自己紹介と経歴",
    aboutCardTitle: "ソフトウェアエンジニア & AI開発者",
    aboutP1: "私のテクノロジーへの道は好奇心から始まり、やがて卓越性を追求するプロとしての仕事へと発展しました。複雑な問題を解決し、コードでアイデアを形にすることにやりがいを感じています。",
    aboutP2: "人工知能、Web開発、モバイル開発、リバースエンジニアリングの分野で、常に影響力があり堅牢なソリューションの構築を目指しています。",
    aboutList: [
      "AIを活用した機能やサービスの設計と実装。",
      "最新のツールを使ったフルスタックWebアプリケーションの開発。",
      "開発者や学習者のための教育・実験用ツールの作成。",
    ],
    projectsTitle: "これまでに作ったもの",
    categories: { web: "Webアプリケーション", mobile: "モバイルアプリ" },
    details: "詳細",
    viewProject: "プロジェクトを見る →",
    visitProject: "プロジェクトを訪問 →",
    clickForDetails: "クリックで詳細を表示",
    openDetails: "{title}の詳細を開く",
    comingSoon: "近日公開",
    soon: "近日公開",
    soundOn: "音声をオン",
    soundOff: "音声をオフ",
    close: "閉じる",
    projects: {
      android: {
        subtitle: "インタラクティブなAndroid起動シーケンス",
        description: "Androidの起動シーケンスを体験できるインタラクティブなシミュレーター。ステップごとの可視化、ターミナル画面、システム初期化についての学習コンテンツを備えています。",
        details: [
          "ブートローダーからホーム画面まで、起動の各段階をステップごとに可視化。",
          "起動ログをリアルタイムで表示する内蔵ターミナル。",
          "各ステップでAndroidが何をしているかを説明する解説。",
        ],
      },
      youngme: {
        subtitle: "求人マッチングプラットフォーム",
        description: "YoungME Lavoroは、若い求職者と企業・行政をつなぎ、最適な仕事の機会を見つける手助けをするプラットフォームです。",
        details: [
          "求職者と企業・行政をマッチング。",
          "求職者、企業、公的機関それぞれの専用スペース。",
        ],
      },
      fantazanca: {
        subtitle: "マルチプラットフォームのファンタジーゲーム",
        description: "FantaZancaは、2026年メッシーナ市議会選挙をテーマにした初のファンタジーゲームです。実際の候補者からチームを作り、選挙戦を追いかけ、市のランキングで上位を目指しましょう。",
        details: [
          "実際の選挙候補者の中からチームを編成。",
          "選挙戦を追いかけてポイントを獲得。",
          "他のプレイヤーと競い、市のランキングを駆け上がろう。",
        ],
      },
      tripsters: {
        subtitle: "地元の人のように旅しよう",
        description: "Tripstersは、地元の人が案内するオーダーメイドの旅行プランを、メッシーナの中心から世界中へ届けます。そこに本当に住んでいる人とチャットして、実際に会いましょう。",
        details: [
          "旅行者は認証済みの地元ガイドを見つけ、リアルタイムでチャットや通話ができます。",
          "チャットや通話の支払いに使えるクレジット式ウォレット。",
          "人間のガイドに加えて、アプリ内のAI旅行アシスタント。",
          "評価とランキングスコアで優秀な地元ガイドを評価。",
        ],
      },
    },
    skillsTitle: "舞台裏の魔法",
    skillGroups: {
      ml: "機械学習とAI",
      algo: "アルゴリズムと分類",
      bigdata: "ビッグデータ",
      langs: "プログラミング言語",
      web: "Web開発",
      security: "サイバーセキュリティ",
      db: "データベース管理",
      linux: "Linuxシステム",
      tools: "ツールとフレームワーク",
      other: "その他のスキル",
    },
    backgroundTitle: "学歴",
    eduTitle: "人工知能の修士号",
    eduText: "アルジェリア・スキクダ大学 • 高度な機械学習、ディープラーニングのアーキテクチャ、AIアルゴリズムを専攻。",
    footerTitle: "一緒に働きましょう",
    footerText: "新しいプロジェクト、クリエイティブなアイデア、インパクトのあるAI製品づくりのご相談をお待ちしています。",
    emailMe: "メールする",
  },
};
